/**
 * Status and state: badges, notices, the empty / error / loading trio, and
 * the spend meter.
 *
 * The rule that runs through all of it: **colour is never the only signal**.
 * A chip carries a word and a shape; a notice carries an icon and a title; a
 * meter carries its numbers in text beside the bar.
 */
import {
  ArrowClockwise,
  CheckCircle,
  Circle,
  Info,
  LockKey,
  MinusCircle,
  Prohibit,
  Warning,
  WarningOctagon,
  type Icon,
} from '@phosphor-icons/react';
import { useState, type CSSProperties, type ReactNode } from 'react';

import { formatPercent } from '@/lib/format';

import { Button } from './button';
import { cx, ui } from './text';

export type Tone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

/* ── Badge ────────────────────────────────────────────────────────── */

export interface BadgeProps {
  tone?: Tone;
  /** An icon in place of the dot. */
  icon?: Icon;
  /** A small filled circle before the label. Ignored when `icon` is set. */
  dot?: boolean;
  /** `outline` for metadata (a vertical, a tier); `soft` (default) for state. */
  appearance?: 'soft' | 'outline';
  children: ReactNode;
}

/** A label, never a button. If it needs to do something, put a button beside it. */
export function Badge({ tone = 'neutral', icon: Glyph, dot = false, appearance = 'soft', children }: BadgeProps) {
  return (
    <span className="badge" data-tone={tone} data-appearance={appearance}>
      {Glyph !== undefined ? <Glyph size={14} weight="bold" aria-hidden="true" /> : dot ? <span className="badge-dot" aria-hidden="true" /> : null}
      <span>{children}</span>
    </span>
  );
}

/* ── StatusChip ───────────────────────────────────────────────────── */

const STATUS_ICON: Record<Exclude<Tone, 'accent'>, Icon> = {
  neutral: MinusCircle,
  success: CheckCircle,
  warning: Warning,
  danger: Prohibit,
  info: Circle,
};

/**
 * A lifecycle state (نشطة · موقوفة · مدعوّ). The icon is chosen by tone so
 * that "active" and "suspended" differ in shape as well as colour — legible
 * to a colour-blind operator and in a greyscale screenshot.
 */
export function StatusChip({ tone, label }: { tone: Exclude<Tone, 'accent'>; label: string }) {
  return (
    <Badge tone={tone} icon={STATUS_ICON[tone]}>
      {label}
    </Badge>
  );
}

/* ── Notice ───────────────────────────────────────────────────────── */

export type NoticeTone = 'info' | 'success' | 'warning' | 'danger' | 'boundary';

const NOTICE_ICON: Record<NoticeTone, Icon> = {
  info: Info,
  success: CheckCircle,
  warning: Warning,
  danger: WarningOctagon,
  boundary: LockKey,
};

export interface NoticeProps {
  /**
   * `boundary` is the content-isolation note (ATA-36): put it where the
   * counts are, saying that contents are not readable from here. It is a
   * statement of design, not an error, so it is calm and never dismissible.
   */
  tone?: NoticeTone;
  title?: ReactNode;
  children?: ReactNode;
  /** A button or link at the inline end. */
  action?: ReactNode;
  /** Tighter padding, for inside a card. */
  compact?: boolean;
}

export function Notice({ tone = 'info', title, children, action, compact = false }: NoticeProps) {
  const Glyph = NOTICE_ICON[tone];

  return (
    <div className={cx('notice', compact && 'notice-compact')} data-tone={tone} role={tone === 'danger' ? 'alert' : undefined}>
      <Glyph className="notice-icon" size={20} weight={tone === 'boundary' ? 'regular' : 'fill'} aria-hidden="true" />
      <div className="notice-body">
        {title === undefined ? null : <p className="notice-title">{title}</p>}
        {children === undefined ? null : <div className="notice-text">{children}</div>}
      </div>
      {action === undefined ? null : <div className="notice-action">{action}</div>}
    </div>
  );
}

/* ── EmptyState / ErrorState ──────────────────────────────────────── */

export interface EmptyStateProps {
  icon?: Icon;
  title: string;
  /** The next step, not a restatement of the title. */
  body?: ReactNode;
  action?: ReactNode;
  /** For inside a table cell or a small card. */
  compact?: boolean;
}

export function EmptyState({ icon: Glyph, title, body, action, compact = false }: EmptyStateProps) {
  return (
    <div className={cx('state', compact && 'state-compact')}>
      {Glyph === undefined ? null : (
        <span className="state-icon">
          <Glyph size={compact ? 22 : 28} aria-hidden="true" />
        </span>
      )}
      <p className="state-title">{title}</p>
      {body === undefined ? null : <p className="state-body">{body}</p>}
      {action === undefined ? null : <div className="state-action">{action}</div>}
    </div>
  );
}

export interface ErrorStateProps {
  /** The API's own message (`messageOf(error)`), never a generic one. */
  message: string;
  title?: string;
  onRetry?: () => void;
  retrying?: boolean;
  compact?: boolean;
}

export function ErrorState({ message, title, onRetry, retrying = false, compact = false }: ErrorStateProps) {
  return (
    <div className={cx('state', 'state-error', compact && 'state-compact')} role="alert">
      <span className="state-icon">
        <WarningOctagon size={compact ? 22 : 28} aria-hidden="true" />
      </span>
      <p className="state-title">{title ?? ui('errorTitle')}</p>
      <p className="state-body">{message}</p>
      {onRetry === undefined ? null : (
        <div className="state-action">
          <Button size="sm" icon={ArrowClockwise} loading={retrying} onClick={onRetry}>
            {ui('retry')}
          </Button>
        </div>
      )}
    </div>
  );
}

/* ── Skeleton ─────────────────────────────────────────────────────── */

export interface SkeletonProps {
  /** Any CSS length. Defaults to the container. */
  width?: string;
  /** Defaults to one line of body text. */
  height?: string;
  shape?: 'line' | 'block' | 'circle';
}

/** A placeholder in the shape of what is coming. Hidden from assistive tech; the region says `aria-busy`. */
export function Skeleton({ width, height, shape = 'line' }: SkeletonProps) {
  const style: CSSProperties = {};

  if (width !== undefined) style.inlineSize = width;
  if (height !== undefined) style.blockSize = height;

  return <span className="skeleton" data-shape={shape} style={style} aria-hidden="true" />;
}

/** A paragraph's worth of lines, the last one short. */
export function SkeletonText({ lines = 3 }: { lines?: number }) {
  return (
    <span className="skeleton-text" aria-hidden="true">
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton key={index} width={index === lines - 1 && lines > 1 ? '60%' : '100%'} />
      ))}
    </span>
  );
}

/* ── Resource ─────────────────────────────────────────────────────── */

export interface ResourceProps<T> {
  /** `undefined` while loading; the value once it arrives. */
  data: T | undefined;
  error?: string | null;
  onRetry?: () => void;
  /** What to draw while loading — a skeleton in the shape of the content. */
  loading: ReactNode;
  /** When true for the arrived data, `empty` is drawn instead of `children`. */
  isEmpty?: (data: T) => boolean;
  empty?: ReactNode;
  children: (data: T) => ReactNode;
}

/**
 * The four states of anything fetched, in one place: error wins, then
 * loading, then empty, then content. A page that uses this cannot forget one.
 */
export function Resource<T>({ data, error = null, onRetry, loading, isEmpty, empty, children }: ResourceProps<T>) {
  if (error !== null) return onRetry === undefined ? <ErrorState message={error} /> : <ErrorState message={error} onRetry={onRetry} />;
  if (data === undefined) return <div aria-busy="true">{loading}</div>;
  if (isEmpty !== undefined && empty !== undefined && isEmpty(data)) return <>{empty}</>;

  return <>{children(data)}</>;
}

/* ── Meter ────────────────────────────────────────────────────────── */

export interface MeterProps {
  /** Accessible name, e.g. "الإنفاق هذا الشهر". */
  label: string;
  value: number;
  /**
   * `cap` mode: the cap, or `null` for no cap (the bar is drawn empty and the
   * text says so). `share` mode: the whole that `value` is a part of — `null`
   * when the whole is not known, and only the count is shown.
   */
  max: number | null;
  /**
   * `cap` (default) — spend against a limit. Turns amber at `warnAt` with
   * «يقترب من السقف» and red at 100% with «تجاوز السقف».
   *
   * `share` — a part of a whole: «٧ من ١٢ منشأة · ٥٨٪». No limit, so no
   * warning: 100% means "all of them", which is not an overrun, and the bar
   * never turns amber or red.
   */
  mode?: 'cap' | 'share';
  /** Formats both numbers in the text line (money, counts). */
  format?: (value: number) => string;
  /** `cap` only: share of the cap at which it turns amber. Red is always ≥ 100%. */
  warnAt?: number;
  /** Hide the visible label (still announced) — for a table cell. */
  hideLabel?: boolean;
  size?: 'sm' | 'md';
}

/**
 * Spend against a cap, or a share of a whole. In `cap` mode the state is said
 * in words (يقترب من السقف · تجاوز السقف), not only by the bar turning red.
 */
export function Meter({ label, value, max, mode = 'cap', format = String, warnAt = 0.8, hideLabel = false, size = 'md' }: MeterProps) {
  const ratio = max === null || max <= 0 ? 0 : value / max;
  const share = mode === 'share';
  const tone = share ? 'accent' : max === null ? 'neutral' : ratio >= 1 ? 'danger' : ratio >= warnAt ? 'warning' : 'accent';
  const status = share ? null : tone === 'danger' ? ui('meterOver') : tone === 'warning' ? ui('meterNear') : null;
  const percent = share && max !== null && max > 0 ? formatPercent(Math.min(ratio, 1)) : null;
  const text =
    max === null ? (share ? format(value) : `${format(value)} · ${ui('meterNoCap')}`) : ui('meterOf', { value: format(value), max: format(max) });
  const valueText = [text, percent, status].filter((part): part is string => part !== null).join(' — ');

  return (
    <div className="meter" data-tone={tone} data-size={size} data-mode={mode}>
      <div className={cx('meter-head', hideLabel && 'sr-only')}>
        <span className="meter-label">{label}</span>
      </div>
      <div
        className="meter-track"
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={max ?? 0}
        aria-valuenow={share && max !== null ? Math.min(value, max) : value}
        aria-valuetext={valueText}
      >
        <span className="meter-fill" style={{ '--meter-value': `${Math.min(ratio, 1) * 100}%` } as CSSProperties} />
      </div>
      <div className="meter-foot">
        <span className="num">{text}</span>
        {percent === null ? null : <span className="num">{percent}</span>}
        {status === null ? null : (
          <span className="meter-status">
            <Warning size={14} weight="fill" aria-hidden="true" />
            {status}
          </span>
        )}
      </div>
    </div>
  );
}

/* ── Progress ─────────────────────────────────────────────────────── */

export interface ProgressProps {
  /** What is in progress: «رفع الشعار». The accessible name. */
  label: string;
  /** 0 to 1. `null` while the length is unknown (hashing, waiting for the server): a sliding bar. */
  value: number | null;
  /** The line under the bar: «جارٍ الرفع…», the API's error. Defaults to the percentage. */
  status?: ReactNode;
  /** `danger` once it failed (say why in `status`); `success` once it is done. */
  tone?: 'accent' | 'success' | 'danger';
  /** Hide the visible label (still announced). */
  hideLabel?: boolean;
}

/**
 * Work under way — an upload. Not a `Meter`: nothing here is a limit. Its
 * state is in words beside the bar (`status`), and the bar is a real
 * `progressbar`, so a screen reader hears the percentage.
 */
export function Progress({ label, value, status, tone = 'accent', hideLabel = false }: ProgressProps) {
  const clamped = value === null ? null : Math.max(0, Math.min(value, 1));
  const percent = clamped === null ? null : formatPercent(clamped);

  return (
    <div className="progress" data-tone={tone} data-indeterminate={clamped === null || undefined}>
      <span className={cx('meter-label', hideLabel && 'sr-only')}>{label}</span>
      <div
        className="progress-track"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={clamped === null ? undefined : Math.round(clamped * 100)}
        aria-valuetext={percent ?? undefined}
        aria-busy={clamped === null || undefined}
      >
        <span className="progress-fill" style={clamped === null ? undefined : ({ '--progress-value': `${clamped * 100}%` } as CSSProperties)} />
      </div>
      {status === undefined && percent === null ? null : (
        <div className="progress-foot">
          <span>{status ?? null}</span>
          {percent === null ? null : <span className="num">{percent}</span>}
        </div>
      )}
    </div>
  );
}

/* ── Avatar ───────────────────────────────────────────────────────── */

export interface AvatarProps {
  initials: string;
  size?: 'sm' | 'md' | 'lg';
  /**
   * A logo or photo. Shown when it loads; the initials stay underneath and are
   * what remains when it 404s or fails, so a missing logo is never a broken
   * image. Public, unauthenticated URLs only — an `<img>` sends no bearer.
   */
  src?: string | null | undefined;
  /** `square` for an organisation's logo, `circle` for a person. */
  shape?: 'circle' | 'square';
}

/** Initials (or an image over them). Decorative — the name is always printed beside it. */
export function Avatar({ initials, size = 'md', src, shape = 'circle' }: AvatarProps) {
  const [failed, setFailed] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<string | null>(null);
  const showImage = src !== undefined && src !== null && src !== '' && failed !== src;

  return (
    <span className="avatar" data-size={size} data-shape={shape} data-image={showImage && loaded === src ? '' : undefined} aria-hidden="true">
      {/* A zero-width non-joiner between letters: Arabic initials would otherwise join into a word. */}
      <span className="avatar-initials">{[...initials].join('\u200c')}</span>
      {showImage ? (
        <img
          className="avatar-image"
          src={src}
          alt=""
          decoding="async"
          onLoad={() => setLoaded(src)}
          onError={() => setFailed(src)}
        />
      ) : null}
    </span>
  );
}
